var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Link, Stack, styled } from '@mui/material';
import { isEmpty, isNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import yup from '../../config/yup';
import { RumEntityLabel } from '../../observability/rumEntity';
import { FormField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '../common';
import { ALL_LOCATIONS_ID, FULL_DATE_TIME_FORMAT, MAXIMUM_VALUES, METRIC_FORMATTING_OPTIONS, PERCENTAGES, PERIOD_BASE_ROWS, PERIOD_WORK_OFFERED_HANDLED_ROWS, } from './constants';
import { StyledNativeNumberInput } from './dataPanel';
import { ACTUAL_ATTRITION_MODE, ACTUAL_DATA_FIELDS, FREQUENCY_ENUM, METRICS_ENUM, PERIOD_DATA_FIELDS } from './enum';
import { getFormatterValueByMetric, usePlanColorById, useReportSummaryData, useReportTimelineOptions, useSelectIntervalTime, useSelectScenarioById, useUpdateActualDataManual, useVarianceColor, } from './selectors';
var STICKIES = ['10.75rem'];
var StyledTableContainer = styled(TableContainer)({
    borderRadius: 0,
    border: 'unset',
});
export var StyledFirstCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        textAlign: 'left',
        minWidth: theme.typography.pxToRem(400),
        '&.MuiTableCell-root': {
            borderLeft: "1px solid ".concat(theme.palette.grey[600]),
        },
    });
});
var StyledHeaderCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.common.white,
        minWidth: theme.typography.pxToRem(200),
    });
});
var StyledDataCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        minWidth: theme.typography.pxToRem(200),
        backgroundColor: theme.palette.common.white,
        position: 'relative',
    });
});
var StyledStack = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        width: '100%',
        height: '98%',
        position: 'absolute',
        top: 0,
        left: 0,
        padding: theme.spacing(0, 2),
        justifyContent: 'center',
        alignItems: 'center',
    });
});
var TableAction = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        marginTop: theme.spacing(4),
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: theme.spacing(4),
    });
});
var StyledButton = styled(LoadingButton)(function () { return ({
    textTransform: 'uppercase',
    minWidth: '6.25rem',
}); });
export var PeriodTableHead = function (_a) {
    var data = _a.data, timelines = _a.timelines;
    var t = useTranslation('report', { keyPrefix: 'actual.periodCompareTable' }).t;
    var watch = useFormContext().watch;
    var planId = watch().planId;
    var headColor = usePlanColorById(planId || 0)[0];
    var scenario = useSelectScenarioById(planId || 0);
    return (_jsxs(TableHead, { children: [_jsxs(TableRow, __assign({ sx: { background: 'blue' } }, { children: [_jsx(StyledFirstCell, { stickySizes: STICKIES }), timelines.map(function (timeline, cellIdx) {
                        var isPlan = data.dividerIndex === -1 || (data.dividerIndex !== null && cellIdx > data.dividerIndex);
                        return (_jsx(StyledHeaderCell, __assign({ highlightBorder: cellIdx === data.dividerIndex, sx: function (theme) { return ({
                                backgroundColor: isPlan ? "".concat(theme.palette.grey[900], " !important") : '',
                            }); } }, { children: isPlan ? t('planned') : t('actuals') }), timeline.id));
                    })] })), _jsxs(TableRow, { children: [_jsx(StyledFirstCell, __assign({ stickySizes: STICKIES, color: headColor }, { children: _jsx(RumEntityLabel, __assign({ kind: "scenario", id: (scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) ? undefined : scenario === null || scenario === void 0 ? void 0 : scenario.id }, { children: scenario === null || scenario === void 0 ? void 0 : scenario.name })) })), timelines.map(function (timeline, cellIdx) { return (_jsx(StyledHeaderCell, __assign({ color: headColor, highlightBorder: cellIdx === data.dividerIndex }, { children: timeline.table }), timeline.id)); })] })] }));
};
export var PeriodTableBody = function (_a) {
    var rows = _a.rows, data = _a.data, timelines = _a.timelines, methods = _a.methods, _b = _a.disabled, disabled = _b === void 0 ? false : _b;
    var t = useTranslation('report', { keyPrefix: 'actual.periodCompareTable' }).t;
    var watch = useFormContext().watch;
    var _c = watch(), metricTab = _c.metricTab, planId = _c.planId, actualAttritionMode = _c.actualAttritionMode, queueLocationId = _c.queueLocationId;
    var _d = usePlanColorById(planId || 0), bodyColor = _d[1];
    var getVarianceColor = useVarianceColor(metricTab);
    var getAbandonmentRateColor = useVarianceColor(METRICS_ENUM.ABANDONMENT_RATE);
    var details = data.details, _e = data.editableRows, editableRows = _e === void 0 ? [] : _e;
    var isAbsoluteAttrition = metricTab === METRICS_ENUM.ATTRITION && actualAttritionMode === ACTUAL_ATTRITION_MODE.ABSOLUTE;
    var getCommonStylingTableCell = function (row, theme, value, isData) {
        if (row.styled) {
            return row.styled(theme, value, isData);
        }
        return { backgroundColor: bodyColor, fontWeight: 'bold' };
    };
    var getColorTableCell = function (row, value) {
        var varianceStyledFields = [PERIOD_DATA_FIELDS.VARIANCE, PERIOD_DATA_FIELDS.ABANDONMENT_RATE];
        if (!isNumber(value) || !row.id || !varianceStyledFields.includes(row.id)) {
            return '';
        }
        if (row.id === PERIOD_DATA_FIELDS.ABANDONMENT_RATE) {
            return getAbandonmentRateColor(value);
        }
        return getVarianceColor(value);
    };
    return (_jsx(FormProvider, __assign({}, methods, { children: _jsx(TableBody, { children: rows
                .filter(function (row) {
                return queueLocationId === undefined || queueLocationId === -1 || row.id !== PERIOD_DATA_FIELDS.ABANDONMENT_RATE;
            })
                .map(function (row, rowIdx) {
                var _a;
                var isEditableRow = row.id === PERIOD_DATA_FIELDS.ACTUAL && metricTab === METRICS_ENUM.ATTRITION;
                return (_jsxs(TableRow, { children: [_jsx(StyledFirstCell, __assign({ stickySizes: STICKIES, sx: function (theme) { return getCommonStylingTableCell(row, theme, null, false); } }, { children: t("headerKey.".concat(row.id || metricTab)) })), timelines.map(function (timeline, colIdx) {
                            var _a, _b, _c, _d;
                            var value = row.id ? (_a = details[row.id]) === null || _a === void 0 ? void 0 : _a[colIdx] : null;
                            var isEditableCol = isEditableRow && editableRows.includes(colIdx);
                            if (!row.id) {
                                return (_jsx(StyledDataCell, { highlightBorder: colIdx === data.dividerIndex, sx: { backgroundColor: bodyColor } }, timeline.id));
                            }
                            return (_jsx(StyledDataCell, __assign({ highlightBorder: colIdx === data.dividerIndex, sx: function (theme) { return (__assign(__assign({}, (isEditableCol ? {} : getCommonStylingTableCell(row, theme, value, true))), { color: getColorTableCell(row, value) })); } }, { children: isEditableCol ? (_jsx(StyledStack, { children: _jsx(FormField, __assign({ sx: { width: '100%' }, name: "".concat(ACTUAL_DATA_FIELDS.UPSERT_DATASET, ".").concat(timeline.id), defaultValue: getFormatterValueByMetric(value, metricTab, actualAttritionMode) }, { children: _jsx(StyledNativeNumberInput, __assign({}, METRIC_FORMATTING_OPTIONS[metricTab], MAXIMUM_VALUES[metricTab], { suffixDefault: isAbsoluteAttrition ? '' : (_b = METRIC_FORMATTING_OPTIONS[metricTab]) === null || _b === void 0 ? void 0 : _b.suffixDefault, decimal: (_c = METRIC_FORMATTING_OPTIONS[metricTab]) === null || _c === void 0 ? void 0 : _c.decimal, max: isAbsoluteAttrition ? 9999 : (_d = MAXIMUM_VALUES[metricTab]) === null || _d === void 0 ? void 0 : _d.max, setValue: methods.setValue, disabled: disabled })) })) })) : (row.render(value, metricTab, actualAttritionMode)) }), timeline.id));
                        })] }, (_a = row.id) !== null && _a !== void 0 ? _a : rowIdx));
            }) }) })));
};
export var validateForm = yup.object((_a = {},
    _a[ACTUAL_DATA_FIELDS.UPSERT_DATASET] = yup.object().required(),
    _a));
export var PeriodCompareTable = function (_a) {
    var _b;
    var _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.clearDisabled, clearDisabled = _d === void 0 ? disabled : _d, onOpenClearDialog = _a.onOpenClearDialog;
    var t = useTranslation('report', { keyPrefix: 'actual.dailyCompareTable' }).t;
    var data = useReportSummaryData();
    var details = data.details, editableRows = data.editableRows;
    var watch = useFormContext().watch;
    var _e = watch(), metricTab = _e.metricTab, periodStart = _e.periodStart, periodEnd = _e.periodEnd, queueId = _e.queueId, queueLocationId = _e.queueLocationId, actualAttritionMode = _e.actualAttritionMode;
    var timelines = useReportTimelineOptions();
    var intervalTime = useSelectIntervalTime(FREQUENCY_ENUM.PERIOD);
    var _f = useUpdateActualDataManual(), actualDataManualUpdated = _f[0], updateActualDataManual = _f[1], clearActualDataManual = _f[2];
    var rows = useMemo(function () {
        return metricTab === METRICS_ENUM.WORK_OFFERED_HANDLED
            ? __spreadArray(__spreadArray([], PERIOD_BASE_ROWS, true), PERIOD_WORK_OFFERED_HANDLED_ROWS, true) : PERIOD_BASE_ROWS;
    }, [metricTab]);
    var timeLineFilterRange = useMemo(function () {
        return timelines.slice(periodStart, periodEnd + 1);
    }, [timelines, periodStart, periodEnd]);
    var initFormValues = useMemo(function () {
        if (!(editableRows === null || editableRows === void 0 ? void 0 : editableRows.length) || isEmpty(details))
            return {};
        var actualRow = details[PERIOD_DATA_FIELDS.ACTUAL];
        var result = Object.fromEntries(timelines
            .filter(function (timeline) { return editableRows.includes(timeline.id); })
            .map(function (timeline) {
            var value = actualRow[timeline.id];
            return [timeline.id, getFormatterValueByMetric(value, metricTab, actualAttritionMode)];
        }));
        return result;
    }, [editableRows, details, timelines, metricTab, actualAttritionMode]);
    var periodMethods = useForm({
        defaultValues: (_b = {},
            _b[ACTUAL_DATA_FIELDS.UPSERT_DATASET] = initFormValues,
            _b),
        resolver: yupResolver(validateForm),
    });
    var reset = periodMethods.reset, periodHandleSubmit = periodMethods.handleSubmit, isDirty = periodMethods.formState.isDirty;
    var resetPeriodMethods = useCallback(function () {
        var _a;
        if (!(editableRows === null || editableRows === void 0 ? void 0 : editableRows.length) || isEmpty(details))
            return;
        reset((_a = {}, _a[ACTUAL_DATA_FIELDS.UPSERT_DATASET] = initFormValues, _a));
    }, [editableRows, details, reset, initFormValues]);
    useEffect(function () {
        resetPeriodMethods();
        clearActualDataManual();
    }, [resetPeriodMethods, clearActualDataManual]);
    var onSave = function (formData) {
        var upsert_dataset = formData[ACTUAL_DATA_FIELDS.UPSERT_DATASET];
        var upsertData = Object.entries(upsert_dataset)
            .map(function (_a) {
            var timelineId = _a[0], value = _a[1];
            var timeline = timelines.find(function (timelineItem) { return timelineItem.id === Number(timelineId); });
            if (!timeline)
                return null;
            var isPercentage = isNumber(value) &&
                (metricTab === METRICS_ENUM.ATTRITION
                    ? actualAttritionMode === ACTUAL_ATTRITION_MODE.PERCENTAGE
                    : metricTab in PERCENTAGES);
            var valueFormatted = isPercentage ? value / 100 : value;
            return {
                value: valueFormatted,
                interval_start: moment(timeline.moment).format(FULL_DATE_TIME_FORMAT),
            };
        })
            .filter(Boolean);
        if (upsertData.length === 0)
            return;
        var payload = __assign({ interval_time: intervalTime, metric_type: metricTab, actual_attrition_mode: actualAttritionMode, upsert_datas: upsertData }, (queueLocationId !== undefined && queueLocationId !== ALL_LOCATIONS_ID
            ? { queue_location_id: queueLocationId }
            : {}));
        updateActualDataManual(payload, { queueId: queueId });
    };
    if (!timeLineFilterRange.length || !rows.length) {
        return null;
    }
    return (_jsxs(Stack, { children: [_jsx(StyledTableContainer, { children: _jsxs(Table, { children: [_jsx(PeriodTableHead, { data: data, timelines: timeLineFilterRange }), _jsx(PeriodTableBody, { data: data, rows: rows, timelines: timeLineFilterRange, methods: periodMethods, disabled: disabled })] }) }), metricTab === METRICS_ENUM.ATTRITION && (_jsxs(TableAction, { children: [_jsx(Link, __assign({ component: "button", type: "button", onClick: resetPeriodMethods, disabled: !isDirty || !(editableRows === null || editableRows === void 0 ? void 0 : editableRows.length) || disabled }, { children: t('reset') })), _jsx(Link, __assign({ component: "button", type: "button", onClick: onOpenClearDialog, disabled: clearDisabled }, { children: t('clear') })), _jsx(StyledButton, __assign({ variant: "outlined", onClick: periodHandleSubmit(onSave), disabled: !isDirty || !(editableRows === null || editableRows === void 0 ? void 0 : editableRows.length) || disabled, loading: actualDataManualUpdated.requesting }, { children: t('save') }))] }))] }));
};
